import dayjs from 'dayjs'
import gql from 'graphql-tag'
import get from 'lodash/get'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import toastr from 'toastr'
import { AutoForm } from 'uniforms-unstyled'
import { editFormDateTimeFields, eventsSummary, prepareToSubmit, withFormStartEnd } from '../../../api/events/helpers'
import { eventFragment } from '../../../graphql/fragments'
import useCrossZoneConfirm from '../../../hooks/useCrossZoneConfirm'
import useFreshBuildSubmit from '../../../hooks/useFreshBuildSubmit'
import useOnImageUploadStateChange from '../../../hooks/useOnImageUploadStateChange'
import useRecordSubmissionMutation from '../../../hooks/useRecordSubmissionMutation'
import useUserOrganizations from '../../../hooks/useUserOrganizations'
import { Event } from '../../../models'
import { CurrentUserContext } from '../../../providers/CurrentUserContext'
import { EventFormSchema } from '../../../schemas/events/eventSchema'
import Routes from '../../../startup/routes'
import { applyTempPlaceChange } from '../../../util/crossZoneConfirm'
import { formatPhoneForForm } from '../../../util/string'
import UniformsButton from '../form-fields/uniform-fields/UniformsButton'
import Loading from '../misc/Loading'
import CrossZoneConfirmModal from './CrossZoneConfirmModal'
import EventFields from './EventFields'
import { OrganizerType } from './OrganizerTypeSelector'
import { ResolvedZoneContext } from './ResolvedZoneContext'

// Snap a time string like "5:43pm" to the nearest 15-minute increment ("5:45pm")
// so it matches the form's allowedValues for the time select dropdowns.
const snapToQuarterHour = (time: string): string => {
  const parsed = dayjs('12-17-1986 ' + time, 'MM-DD-YYYY h:mma')
  if (!parsed.isValid()) return time
  const minute = parsed.minute()
  const snapped = Math.round(minute / 15) * 15
  const adjusted = parsed.minute(0).add(snapped, 'minute')
  return adjusted.format('h:mma')
}

const EVENT_EDIT = gql`
  mutation EventUpdate($input: EventUpdateInput!) {
    eventUpdate(input: $input) {
      event {
        ...eventFragment
      }
      errors {
        attribute
        message
      }
    }
  }
  ${eventFragment}
`

type Props = { event: Event; loading: boolean }
const EventEdit: React.FC<Props> = ({ event: originalEvent, loading }) => {
  const history = useHistory()
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const formRef = useRef(null)

  const { currentUser } = useContext(CurrentUserContext)
  const { organizations: userOrganizations, loading: orgsLoading } = useUserOrganizations()

  // Include the event's organization in the list (for editing events submitted on behalf of an org)
  const organizations = useMemo(() => {
    const eventOrg = originalEvent.submittedOnBehalfOfOrg
    if (!eventOrg) return userOrganizations

    // Check if the event's org is already in user's organizations
    const alreadyIncluded = userOrganizations.some((org) => org.id === eventOrg.id)
    if (alreadyIncluded) return userOrganizations

    // Add the event's organization to the list
    return [
      ...userOrganizations,
      {
        id: eventOrg.id,
        name: eventOrg.name,
        contactEmail: eventOrg.contactEmail || null,
        phone: eventOrg.phone || null,
      },
    ]
  }, [userOrganizations, originalEvent.submittedOnBehalfOfOrg])

  // Derive organizerType from existing event data
  const eventWithOrganizerType = useMemo(() => {
    let organizerType: OrganizerType = 'myself'
    let selectedOrganizationId: string | null = null

    if (originalEvent.submittedOnBehalfOfOrg?.id) {
      organizerType = 'organization'
      selectedOrganizationId = originalEvent.submittedOnBehalfOfOrg.id
    } else if (originalEvent.submitterOwnsEvent === false) {
      organizerType = 'other'
    }

    // Populate split date/time fields as plain properties so Uniforms can read them.
    // The Event class has these as prototype getter/setters (via applyMixins), but the
    // spread creates a plain object that loses them. We reconstruct them here.
    //
    // Phase 9 (design rev.5 §1 Outbound): seed the split date/time fields from
    // the server's venue-zone wall-clock (startAtLocal/endAtLocal, Phase 6) via
    // editFormDateTimeFields — which uses LOCAL construction, NOT the
    // browser-re-zoned ISO-instant Date from model.ts. This makes the editor see
    // the same wall-clock the public feed shows; a no-op save then re-derives
    // the SAME *_local (prepareToSubmit, Phase 8a) so EventUpdate#resolve
    // re-resolves the SAME zone and stores the SAME UTC instant (round-trip:
    // out == in). Falls back to the legacy ISO Date when *_local is absent (old
    // cached server — additive rollout safety). Times are snapped to 15-minute
    // increments to match the form's allowedValues.
    const hasDateTime = originalEvent.startAtLocal || originalEvent.startAt
    const dt = hasDateTime ? editFormDateTimeFields(originalEvent) : null

    return {
      ...originalEvent,
      organizerType,
      selectedOrganizationId,
      ...(dt && {
        startAtDate: dt.startAtDate,
        startAtTime: snapToQuarterHour(dt.startAtTime),
        endAtDate: dt.endAtDate,
        endAtTime: snapToQuarterHour(dt.endAtTime),
      }),
    }
  }, [originalEvent])

  const navigate = useCallback(() => history.push(Routes.yourEvents()), [history])

  const beforeSubmit = useCallback((doc) => prepareToSubmit(doc), [])

  const onSuccess = useCallback(() => {
    toastr.success('Event updated!')
    navigate()
  }, [navigate])

  const {
    handleSubmit,
    mutationLoading: recordUpdateMutationLoading,
    onValidate,
  } = useRecordSubmissionMutation({ query: EVENT_EDIT, queryName: 'eventUpdate', recordName: 'event', beforeSubmit, onSuccess })

  // Phase 10b (design rev.5 §5) — cross-zone explicit save confirmation on the
  // EDIT path. The confirm states EXACTLY what the save will store for the
  // CURRENT in-form venue: useCrossZoneConfirm queries
  // editResolvedTimeZone(eventId, placeId: currentPlaceId), which delegates to
  // the SAME Events::ResolvedLeadZone EventUpdate#resolve uses (single source
  // of truth — no drift; a mandatory parity rspec proves it). One server
  // value is truthful for EVERY sub-case — geocoded venue (venue zone),
  // blank-geo venue (persisted column — the 10a residual, now closed),
  // no/removed venue (persisted column per Phase-9b), unchanged (same). The
  // venue field (RecordSearchAutocomplete bound to placeId) is tracked via
  // the <AutoForm> onChange below so the query re-fires on a venue change.
  // eventId is the persisted event's global id (loads: Types::EventType
  // server-side — same as EventUpdate's id arg).
  const [currentPlaceId, setCurrentPlaceId] = useState<string | null>(originalEvent.place?.id ?? null)
  // #898 item 1: the CURRENT typed temp-venue state/city (seeded from the
  // event's nested tempPlace.address), so the EDIT confirm resolves a typed
  // venue's zone == what EventUpdate stores.
  const seededEditTempAddress = (originalEvent as { tempPlace?: { address?: { state?: string; city?: string } } })
    .tempPlace?.address
  const [currentTempPlaceState, setCurrentTempPlaceState] = useState<string | null>(
    seededEditTempAddress?.state ?? null,
  )
  const [currentTempPlaceCity, setCurrentTempPlaceCity] = useState<string | null>(seededEditTempAddress?.city ?? null)
  // Only the venue saving will use (prepareToSubmit): the picked venue, or the typed-in one once
  // "not in directory" is ticked, never both.
  const [useTempPlace, setUseTempPlace] = useState<boolean>(Boolean(originalEvent.useTempPlace))
  const { guardSubmit, modalProps, resolvedZone, checkingZone } = useCrossZoneConfirm({
    mode: 'edit',
    // The GraphQL `id` (IdentifiableType -> numeric DB id) — passed as-is,
    // exactly like the EventUpdate `id` arg and Phase-7 `placeId` (both use
    // numeric ids via `loads:`). The query's eventId arg `loads:
    // Types::EventType` server-side, same as EventUpdateInput's id arg.
    eventId: originalEvent.id ? String(originalEvent.id) : null,
    currentPlaceId: useTempPlace ? null : currentPlaceId,
    currentTempPlaceAddressState: useTempPlace ? currentTempPlaceState : null,
    currentTempPlaceAddressCity: useTempPlace ? currentTempPlaceCity : null,
  })
  // Reload a stale tab instead of letting it submit the pre-redesign format
  // (which bakes the wall-clock in the browser's zone, not the venue's).
  const withFreshBuild = useFreshBuildSubmit()
  const onSubmit = useCallback(
    (doc: unknown) => withFreshBuild(() => guardSubmit(handleSubmit)(doc)),
    [withFreshBuild, guardSubmit, handleSubmit],
  )

  // Track the in-form venue selection so the cross-zone confirm reflects the
  // CURRENT venue (EventUpdateInput has no community/primary_community arg, so
  // community can't change via EventUpdate — only placeId matters on edit).
  const onChange = useCallback((key: string, value: unknown) => {
    if (key === 'placeId') {
      setCurrentPlaceId((value as string) || null)
    } else if (key === 'useTempPlace') {
      setUseTempPlace(Boolean(value))
    } else if (key.split('.')[0] === 'tempPlace') {
      applyTempPlaceChange(key, value, setCurrentTempPlaceState, setCurrentTempPlaceCity)
    }
  }, [])

  const onImageUploadStateChange = useOnImageUploadStateChange(setIsSubmitDisabled)

  const onOrganizerTypeChange = useCallback(
    (type: OrganizerType, selectedOrgId: string | null) => {
      const formReference = formRef.current
      if (!formReference) return

      formReference.change('organizerType', type)
      formReference.change('selectedOrganizationId', selectedOrgId)

      if (type === 'myself') {
        // Pre-fill with current user info and set submitterOwnsEvent
        formReference.change('submitterOwnsEvent', true)
        formReference.change('organizer.name', get(currentUser, 'fullName'))
        formReference.change('organizer.email', get(currentUser, 'email'))
        formReference.change('organizer.phone', '')
      } else if (type === 'organization' && selectedOrgId) {
        // Pre-fill with organization info and clear submitterOwnsEvent
        formReference.change('submitterOwnsEvent', false)
        const org = organizations.find((o) => o.id === selectedOrgId)
        if (org) {
          formReference.change('organizer.name', org.name || '')
          formReference.change('organizer.email', org.contactEmail || '')
          formReference.change('organizer.phone', formatPhoneForForm(org.phone))
        }
      } else if (type === 'other') {
        // Clear for manual entry and mark as not submitter-owned
        formReference.change('submitterOwnsEvent', false)
        formReference.change('organizer.name', '')
        formReference.change('organizer.email', '')
        formReference.change('organizer.phone', '')
      }
    },
    [currentUser, organizations],
  )

  if (loading || orgsLoading) return <Loading className="isDark" />

  // Phase 10 (modal date/time): the cross-zone confirm shows the full date +
  // time being saved (reusing `eventsSummary`, the same helper the on-form
  // Summary box uses) so the submitter can confirm exactly what they commit.
  // CVL-122: built from the times CURRENTLY in the form (computed at render —
  // the modal only opens after Submit, as on EventNew), merged from the split
  // venue-zone date/time fields. The model's startAt/endAt is the page-load
  // instant re-zoned to the computer, which showed the wrong wall-clock.
  const confirmModel = (formRef.current as { getModel: () => Event } | null)?.getModel()
  const whenLabel = modalProps.isOpen && confirmModel ? eventsSummary(withFormStartEnd(confirmModel)) : null

  return (
    <div>
      <CrossZoneConfirmModal {...modalProps} whenLabel={whenLabel} />

      <h1 className="content-form-heading">Edit Event</h1>

      <AutoForm
        className="form content-entry-form event-form"
        model={eventWithOrganizerType}
        onChange={onChange}
        onSubmit={onSubmit}
        onValidate={onValidate}
        ref={formRef}
        schema={EventFormSchema}
      >
        <ResolvedZoneContext.Provider value={resolvedZone}>
          <EventFields
            onImageUploadStateChange={onImageUploadStateChange}
            originalEvent={originalEvent}
            organizations={organizations}
            onOrganizerTypeChange={onOrganizerTypeChange}
          />
        </ResolvedZoneContext.Provider>

        <fieldset className="submit-row">
          <UniformsButton
            disabled={recordUpdateMutationLoading || isSubmitDisabled}
            shouldShowLoadingSpinner={recordUpdateMutationLoading}
            text="Submit Changes"
          />
          {checkingZone ? (
            <span className="submit-row-note" role="status">
              Checking time zone…
            </span>
          ) : null}
        </fieldset>
      </AutoForm>
    </div>
  )
}

export default EventEdit
